import React from "react";

import styles from "./question.module.scss";

import { ReactComponent as Correct } from "../../style/assets/correct.svg";
import { ReactComponent as Incorrect } from "../../style/assets/incorrect.svg";

export type Question = {
    id: string;
    title?: string;
    options: string[];
    style: "pills" | "radio";
    correctOption?: string;
};

type QuestionComponentProps = {
    question: Question;
    setAnswer: (answer: any) => void;
    selectedAnswer: string;
    showCorrectOption?: boolean;
    disabled?: boolean;
};

const QuestionComponent = (props: QuestionComponentProps) => {
    const { question, setAnswer, selectedAnswer, showCorrectOption, disabled } = props;
    return (
        <div className={styles.surveyQuestion}>
            {question.title && <div className={styles.questionText}>{question.title}</div>}
            {question.style === "pills" && (
                <div className={styles.pillContainer} role={"radiogroup"}>
                    {question.options.map((x, i) => {
                        return (
                            <Pill
                                key={`option_${i}_${question.id}`}
                                questionId={question.id}
                                option={x}
                                isSelected={selectedAnswer === x}
                                onSelect={setAnswer}
                            />
                        );
                    })}
                </div>
            )}
            {question.style === "radio" && (
                <div className={styles.radioContainer} role={"radiogroup"} data-id={question.id}>
                    {question.options.map((x, i) => {
                        return (
                            <Radio
                                key={`option_${i}_${question.id}`}
                                questionId={question.id}
                                option={x}
                                isSelected={selectedAnswer === x}
                                isCorrect={
                                    showCorrectOption && question.correctOption
                                        ? question.correctOption === x
                                        : undefined
                                }
                                isDisabled={disabled}
                                onSelect={setAnswer}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const Pill = (props: {
    questionId: string;
    option: string;
    isSelected: boolean;
    onSelect: (answer: any) => void;
}) => {
    const { questionId, option, isSelected, onSelect } = props;
    const onClick = () => {
        onSelect({
            [questionId]: option,
        });
    };
    return (
        <button
            className={styles.pill}
            data-selected={isSelected}
            onClick={onClick}
            aria-checked={isSelected}
            tabIndex={0}
        >
            {option}
        </button>
    );
};

const Radio = (props: {
    questionId: string;
    option: string;
    isSelected: boolean;
    isCorrect?: boolean;
    isDisabled?: boolean;
    onSelect: (answer: any) => void;
}) => {
    const { questionId, option, isSelected, isCorrect, isDisabled, onSelect } = props;
    const onClick = () => {
        onSelect({
            [questionId]: option,
        });
    };
    return (
        <button
            className={styles.radio}
            onClick={!isDisabled ? onClick : undefined}
            data-disabled={isDisabled}
            aria-checked={isSelected}
            tabIndex={0}
        >
            <div
                className={styles.circle}
                data-selected={isSelected}
            />
            {isCorrect === false && isSelected && <Incorrect className={styles.circleIncorrect} />}
            {isCorrect && <Correct className={styles.circleCorrect} />}
            <div className={styles.optionText} data-correct={isCorrect}>
                {option}
            </div>
        </button>
    );
};

export default QuestionComponent;
